/**
 * useWorkspaceIssues — aggregiert per-Channel-Issues workspace-weit.
 * Backend-Aggregat-Endpoint = deklarierter Folge-Backend.
 */
import { useQuery, useQueries } from '@tanstack/react-query'
import {
  getReadinessSummary,
  getReadinessIssues,
  getProductsWithIssues,
  type IssueGroup,
  type ProductWithIssues,
} from '@/lib/api/channel-readiness'
import { mergeIssueGroups } from '@/components/v2/products/issues/aggregateIssues'

export type ByChannel = Record<string, { channelName: string; groups: IssueGroup[]; missingPackshot: number }>

const ISSUES_PAGE_SIZE = 200
// Safety-Bound: max 25 Seiten (≙ 5000 Produkte). Route cappt page_size bei 200,
// daher loopen wir Seiten client-seitig bis `total` erreicht ist. Reale
// Workspaces liegen weit unter 5000; danach brechen wir mit dem bisher
// Geladenen ab (kein endloses Paginieren bei unerwarteten total-Werten).
const MAX_ISSUE_PAGES = 25

async function fetchAllIssueProducts(): Promise<{ products: ProductWithIssues[]; total: number }> {
  const first = await getProductsWithIssues({ page: 1, page_size: ISSUES_PAGE_SIZE })
  const products = [...first.products]
  const total = first.total
  for (let page = 2; page <= MAX_ISSUE_PAGES; page++) {
    if (products.length >= total) break
    const next = await getProductsWithIssues({ page, page_size: ISSUES_PAGE_SIZE })
    if (next.products.length === 0) break
    products.push(...next.products)
  }
  return { products, total }
}

export function useWorkspaceIssues() {
  const summaryQuery = useQuery({
    queryKey: ['readiness-summary'],
    queryFn: async () => (await getReadinessSummary()).summary,
  })

  const channels = summaryQuery.data ?? []

  const issueQueries = useQueries({
    queries: channels.map((ch) => ({
      queryKey: ['readiness-issues', ch.channel_id],
      queryFn: () => getReadinessIssues(ch.channel_id),
      enabled: channels.length > 0,
    })),
  })

  const byChannel: ByChannel = {}
  let missingPackshot = 0

  issueQueries.forEach((q, i) => {
    const ch = channels[i]
    if (!ch || !q.data) return
    byChannel[ch.channel_id] = { channelName: ch.channel_name, groups: q.data.issues, missingPackshot: q.data.missing_packshot_count }
    missingPackshot += q.data.missing_packshot_count
  })

  const groups = mergeIssueGroups(Object.entries(byChannel).map(([id, v]) => ({ channelId: id, groups: v.groups })))
  const totalIssues = groups.reduce((s, g) => s + g.product_count, 0)

  const productsQuery = useQuery({
    queryKey: ['workspace-issues', 'products'],
    queryFn: fetchAllIssueProducts,
  })

  const isLoading =
    summaryQuery.isLoading || issueQueries.some((q) => q.isLoading) || productsQuery.isLoading

  return {
    groups,
    totalIssues,
    missingPackshot,
    isLoading,
    byChannel,
    products: productsQuery.data?.products ?? [],
    productsTotal: productsQuery.data?.total ?? 0,
  }
}
